/**
 * Iter 67bf — Total Cost of Ownership chip.
 *
 * Self-hiding pill that shows YTD Net (Revenue − Expenses) for a single car.
 * Mounted on the public car-detail page and the vendor's car-list cards so
 * vendors/admins can spot loss-making vehicles without opening Fleet finance.
 *
 * The chip silently bails (renders null) on any error — including 403 for
 * non-owners — so it's safe to drop into public pages where customers will
 * never see it.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Money from "@/components/Money";
import { TrendingUp, TrendingDown } from "lucide-react";

export default function CarTcoChip({ carId, compact = false, className = "" }) {
  const [pnl, setPnl] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!carId) return undefined;
    api.get(`/vendor/cars/${carId}/ytd-pnl`)
      .then((r) => { if (alive) setPnl(r.data); })
      .catch(() => { /* 403/404 → render nothing; chip is for owners + admins only */ });
    return () => { alive = false; };
  }, [carId]);

  if (!pnl) return null;
  const net = Number(pnl.net_eur || 0);
  const positive = net >= 0;
  // Mute the chip when there's literally nothing to report — better than
  // showing a "€0.00" with no signal value.
  if (Number(pnl.revenue_eur || 0) === 0 && Number(pnl.expenses_eur || 0) === 0) {
    return null;
  }

  const Icon = positive ? TrendingUp : TrendingDown;
  // Distinct tones for positive vs negative net so the eye can scan a fleet
  // grid and immediately pick out the cars that are bleeding money.
  const tone = positive
    ? "bg-emerald-50 text-emerald-800 ring-emerald-200"
    : "bg-rose-50 text-rose-800 ring-rose-200";

  return (
    <Link
      to="/dashboard/vendor#fleet-finance"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ring-1 text-[11px] font-semibold ${tone} hover:opacity-90 transition ${className}`}
      title={`Revenue €${Number(pnl.revenue_eur).toFixed(2)} − Expenses €${Number(pnl.expenses_eur).toFixed(2)} (open Fleet finance)`}
      data-testid={`car-tco-chip-${carId}`}
      onClick={(e) => e.stopPropagation()}
    >
      <Icon className="h-3 w-3" />
      <span className="uppercase tracking-wider text-[9px] font-bold opacity-70">{compact ? `TCO ${pnl.year}` : `TCO YTD ${pnl.year} · NET`}</span>
      <Money amount={net} className="font-bold tabular-nums" />
    </Link>
  );
}
